// User Menu
import './vendor/jquery-accessibleMegaMenu.js';
import { amplify } from './acs-amplify.js';
import breakpoints from './utils/breakpoints.js';

const DEFAULT_URLS = {
  donate: 'https://www.acs.org/donate.html',
  membership: 'https://www.acs.org/membership.html',
  help: 'https://www.acs.org/help.html',
  logOut: 'https://www.acs.org/bin/acs/sso/session?action=logout',
  logIn: 'https://www.acs.org/bin/acs/sso/login',
  account: 'https://myaccount.acs.org/myaccount/HomePage.html',
  myAcs: 'https://www.acs.org/membership/my-benefits.html',
  upgrade: 'https://www.renew.acs.org',
  rewards: 'https://membership.acs.org/reward/customer/info',
  friend: 'https://membership.acs.org/membership_referral/info',
};

function getAcs() {
  return globalThis.acs || null;
}

function getRuntimeUrls() {
  const sso = getAcs()?.sso || {};
  return {
    donate: sso.acsDonateUrl || DEFAULT_URLS.donate,
    membership: sso.acsMembershipUrl || DEFAULT_URLS.membership,
    help: DEFAULT_URLS.help,
    logOut: sso.localLogout || DEFAULT_URLS.logOut,
    logIn: sso.loginUrl || DEFAULT_URLS.logIn,
    account: sso.acctManagementUrl || DEFAULT_URLS.account,
    myAcs: sso.acsMyBenefitsUrl || DEFAULT_URLS.myAcs,
    upgrade: sso.omrUrl || DEFAULT_URLS.upgrade,
    rewards: DEFAULT_URLS.rewards,
    friend: DEFAULT_URLS.friend,
  };
}

function formatPackage(packagecode) {
  if (!packagecode) return '';
  return `${packagecode.charAt(0).toUpperCase()}${packagecode.slice(1).toLowerCase()} Package`;
}

function getState(user) {
  const isLoggedIn = !!user?.loggedin;
  const packageCode = user?.packagecode || '';
  const packageName = formatPackage(packageCode);
  const firstInitial = (user?.firstname || user?.fullname)?.charAt(0)?.toUpperCase() || '';

  // When login comes from the page-rendered vars we don't know membership/renewal
  // status, so hide anything that depends on it.
  const membershipKnown = user?.membershipKnown !== false;

  const showMembership = membershipKnown && isLoggedIn && (!user?.ismember || packageCode === 'BASIC');
  const showUpgrade = membershipKnown && isLoggedIn && (!!user?.ismember && packageCode !== 'PREMIUM');

  return {
    isLoggedIn,
    firstInitial,
    packageName,
    showMembership,
    showUpgrade,
    showExpiresSoon: membershipKnown && !!(packageName && user?.hasrenewal),
  };
}

// Icons for the new menu only (keyed by link label). Prepended before the text label.
const MENU_ICONS = {
  Account:
    '<svg class="accountnav-link-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M205.5 92.7l13.7-60.7 73.6 0 13.7 60.7c1.7 7.4 6.8 13.6 13.8 16.7 10.8 4.8 21 10.7 30.4 17.6 6.1 4.5 14.1 5.9 21.4 3.6l59.5-18.5 36.8 63.8-45.8 42.3c-5.6 5.2-8.4 12.7-7.6 20.2 1.3 11.5 1.3 23.7 0 35.2-.8 7.6 2 15.1 7.6 20.2l45.8 42.3-36.8 63.8-59.5-18.5c-7.3-2.3-15.2-.9-21.4 3.6-9.4 6.9-19.6 12.9-30.4 17.6-7 3.1-12.1 9.3-13.8 16.7l-13.7 60.7-73.6 0-13.7-60.7c-1.7-7.4-6.8-13.6-13.8-16.7-10.8-4.8-21-10.7-30.4-17.6-6.1-4.5-14.1-5.9-21.4-3.6l-59.5 18.5-36.8-63.8 45.8-42.3c5.6-5.2 8.4-12.7 7.6-20.2-1.3-11.5-1.3-23.7 0-35.2 .8-7.6-2-15.1-7.6-20.2l-45.8-42.3 36.8-63.8 59.5 18.5c7.3 2.3 15.2 .9 21.4-3.6 9.4-6.9 19.6-12.9 30.4-17.6 7-3.1 12.1-9.3 13.8-16.7zM212.8-16c-18.7 0-34.9 13-39 31.2L161.2 70.8c-6.3 3.2-12.3 6.7-18.2 10.5L88.6 64.4c-17.9-5.6-37.2 2-46.5 18.2L-1.1 157.4c-9.3 16.2-6.2 36.7 7.5 49.4l41.9 38.7c-.3 6.9-.3 14 0 21L6.4 305.2c-13.7 12.7-16.9 33.2-7.5 49.4l43.2 74.8c9.3 16.2 28.7 23.7 46.5 18.2l54.5-16.9c5.9 3.8 11.9 7.3 18.2 10.5l12.6 55.6c4.1 18.2 20.3 31.2 39 31.2l86.4 0c18.7 0 34.9-13 39-31.2l12.6-55.6c6.3-3.2 12.3-6.7 18.2-10.5l54.5 16.9c17.9 5.6 37.2-2 46.5-18.2l43.2-74.8c9.3-16.2 6.2-36.7-7.5-49.4l-41.9-38.7c.3-6.9 .3-14 0-21l41.9-38.7c13.7-12.7 16.8-33.2 7.5-49.4L470 82.6c-9.4-16.2-28.7-23.7-46.5-18.2L369 81.3c-5.9-3.8-11.9-7.3-18.2-10.5L338.3 15.2C334.1-3 317.9-16 299.2-16l-86.4 0zM304 256a48 48 0 1 1 -96 0 48 48 0 1 1 96 0zm-48-96a96 96 0 1 0 0 192 96 96 0 1 0 0-192z"></path></svg>',
  'My Benefits':
    '<svg class="accountnav-link-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M288.1-32c9 0 17.3 5.1 21.4 13.1L383 125.3 542.9 150.7c8.9 1.4 16.3 7.7 19.1 16.3s.5 18-5.8 24.4L441.7 305.9 467 465.8c1.4 8.9-2.3 17.9-9.6 23.2s-17 6.1-25 2L288.1 417.6 143.8 491c-8 4.1-17.7 3.3-25-2s-11-14.2-9.6-23.2L134.4 305.9 20 191.4c-6.4-6.4-8.6-15.8-5.8-24.4s10.1-14.9 19.1-16.3l159.9-25.4 73.6-144.2c4.1-8 12.4-13.1 21.4-13.1zm0 76.8L230.3 158c-3.5 6.8-10 11.6-17.6 12.8l-125.5 20 89.8 89.9c5.4 5.4 7.9 13.1 6.7 20.7l-19.8 125.5 113.3-57.6c6.8-3.5 14.9-3.5 21.8 0l113.3 57.6-19.8-125.5c-1.2-7.6 1.3-15.3 6.7-20.7l89.8-89.9-125.5-20c-7.6-1.2-14.1-6-17.6-12.8L288.1 44.8z"></path></svg>',
  'Refer a Friend':
    '<svg class="accountnav-link-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none"><path d="M0.0328125 10.0341C0.164062 9.03875 0.671563 8.16156 1.40656 7.54906C2.08469 6.98688 2.95313 6.65 3.90031 6.65H4.9H5.89969C6.84688 6.65 7.71531 6.98688 8.39125 7.54906C9.12625 8.16156 9.63375 9.03875 9.765 10.0341C9.78687 10.2025 9.79781 10.3753 9.79781 10.5503C9.79781 10.9091 9.50688 11.2 9.14813 11.2H8.71937H1.07844H0.649688C0.290938 11.2 0 10.9091 0 10.5503C0 10.3753 0.0109375 10.2025 0.0328125 10.0341ZM2.1 2.8C2.1 2.05739 2.395 1.3452 2.9201 0.820101C3.4452 0.294999 4.15739 0 4.9 0C5.64261 0 6.3548 0.294999 6.8799 0.820101C7.405 1.3452 7.7 2.05739 7.7 2.8C7.7 3.54261 7.405 4.2548 6.8799 4.7799C6.3548 5.305 5.64261 5.6 4.9 5.6C4.15739 5.6 3.4452 5.305 2.9201 4.7799C2.395 4.2548 2.1 3.54261 2.1 2.8ZM6.65 2.8C6.65 2.33587 6.46563 1.89075 6.13744 1.56256C5.80925 1.23437 5.36413 1.05 4.9 1.05C4.43587 1.05 3.99075 1.23437 3.66256 1.56256C3.33437 1.89075 3.15 2.33587 3.15 2.8C3.15 3.26413 3.33437 3.70925 3.66256 4.03744C3.99075 4.36563 4.43587 4.55 4.9 4.55C5.36413 4.55 5.80925 4.36563 6.13744 4.03744C6.46563 3.70925 6.65 3.26413 6.65 2.8ZM3.90031 7.7C2.85469 7.7 1.9425 8.26219 1.44594 9.1C1.25781 9.415 1.13094 9.77156 1.07844 10.15H2.14375H7.65625H8.72375C8.715 10.0909 8.70625 10.0341 8.69312 9.975C8.62969 9.66219 8.51375 9.36688 8.35406 9.1C7.8575 8.26219 6.94531 7.7 5.89969 7.7H3.90031ZM13.3284 11.2H10.3337C10.4409 11.0075 10.5 10.7866 10.5 10.5503C10.5 9.12844 9.85469 7.85531 8.83969 7.01094C8.93375 7.00219 9.03 7 9.12844 7H10.4716C12.4206 7 14 8.57938 14 10.5284C14 10.9003 13.6981 11.2 13.3284 11.2ZM9.45 5.6C8.77187 5.6 8.15938 5.32438 7.71531 4.88031C8.14625 4.29844 8.4 3.57875 8.4 2.8C8.4 2.21375 8.25563 1.66031 7.99969 1.17469C8.40656 0.877188 8.9075 0.7 9.45 0.7C10.8041 0.7 11.9 1.79594 11.9 3.15C11.9 4.50406 10.8041 5.6 9.45 5.6Z" fill="#251F20"></path></svg>',
  'Reward Points':
    '<svg class="accountnav-link-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM213.4 234.4c15.2-2.4 28.2-11.9 35.2-25.6l7.4-14.4 7.4 14.4c7 13.7 20.1 23.2 35.2 25.6l16 2.5-11.5 11.5c-10.8 10.9-15.8 26.2-13.4 41.4l2.5 16-14.4-7.3c-13.7-7-29.9-7-43.5 0l-14.4 7.3 2.5-16c2.4-15.2-2.6-30.5-13.5-41.4l-11.5-11.5 16-2.5zM306.1 187l-23.2-45.4-.1-.2c-.5-.9-4.7-9.1-12.6-24.7-2.7-5.4-8.2-8.7-14.3-8.7s-11.5 3.4-14.3 8.7l-10 19.7 0 0-2.6 5-.1 .2-23.2 45.4-50.4 8-.2 0c-1 .2-10.1 1.6-27.4 4.4-5.9 .9-10.8 5.1-12.7 10.9s-.4 12 3.9 16.3c29.9 29.9 19.3 19.3 55.8 55.8l-7.9 50.4 0 .2c-.2 1-1.6 10.1-4.3 27.4-.9 5.9 1.5 11.9 6.4 15.4s11.3 4 16.7 1.3c15.6-7.9 23.8-12.1 24.7-12.6l.2-.1 45.4-23.1 45.4 23.1 .2 .1 5 2.6 19.7 10c5.4 2.7 11.8 2.2 16.7-1.3s7.3-9.5 6.4-15.4l-3.4-21.8-.9-5.6 0-.2-7.9-50.4 36-36.1 .1-.1c.7-.7 7.2-7.3 19.6-19.6 4.2-4.3 5.8-10.5 3.9-16.3s-6.8-9.9-12.7-10.9c-41.7-6.6-27-4.3-77.9-12.4z"></path></svg>',
  'Help Center':
    '<svg class="accountnav-link-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M464 256a208 208 0 1 0 -416 0 208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0 256 256 0 1 1 -512 0zm256-80c-17.7 0-32 14.3-32 32 0 13.3-10.7 24-24 24s-24-10.7-24-24c0-44.2 35.8-80 80-80s80 35.8 80 80c0 47.2-36 67.2-56 74.5l0 3.8c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-8.1c0-20.5 14.8-35.2 30.1-40.2 6.4-2.1 13.2-5.5 18.2-10.3 4.3-4.2 7.7-10 7.7-19.6 0-17.7-14.3-32-32-32zM224 368a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"></path></svg>',
  'Log Out':
    '<svg class="accountnav-link-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M280-8c0-13.3-10.7-24-24-24S232-21.3 232-8l0 264c0 13.3 10.7 24 24 24s24-10.7 24-24L280-8zM148.4 78c11.3-6.9 15-21.6 8.1-33s-21.6-15-33-8.1C49.5 81.7 0 163.1 0 256 0 397.4 114.6 512 256 512S512 397.4 512 256c0-92.9-49.5-174.3-123.5-219.1-11.3-6.9-26.1-3.2-33 8.1s-3.2 26.1 8.1 33c60.2 36.5 100.4 102.6 100.4 178 0 114.9-93.1 208-208 208S48 370.9 48 256c0-75.4 40.2-141.5 100.4-178z"></path></svg>',
};

function dropdownItem(hasOldMenu, href, label, oldClass, oldId) {
  if (hasOldMenu) {
    return (
      '<li' +
      (oldId ? ' id="' + oldId + '"' : '') +
      ' class="' +
      oldClass +
      '" role="menuitem" tabIndex="-1"><a href="' +
      href +
      '">' +
      label +
      '</a></li>'
    );
  }
  var icon = MENU_ICONS[label] || '';
  var itemClass = 'my-account__item' + (label === 'Help Center' ? ' accountnav-item-divider' : '');
  return '<li class="' + itemClass + '" role="menuitem"><a href="' + href + '" class="my-account__link">' + icon + label + '</a></li>';
}

export function usermenu() {
  if (typeof acs.ui === 'undefined') {
    acs.ui = {};
  }
  acs.ui.hasOldMenu = !!document.getElementById('my-menu');

  acs.userMenu = {};
  acs.userMenu.init = function () {
    acs.ui.hasOldMenu = !!document.getElementById('my-menu');
    acs.ui.hasNewMenu = !!document.getElementById('member-menu');

    if (!acs.ui.hasOldMenu && !acs.ui.hasNewMenu) {
      return;
    }

    acs.userMenu.build();

    const belowMediumQuery = breakpoints.below(breakpoints.BREAKPOINT_NAVIGATION);
    const aboveMediumQuery = breakpoints.above(breakpoints.BREAKPOINT_NAVIGATION);

    if (belowMediumQuery.matches) {
      acs.userMenu.build();
      acs.userMenu.mobile();
    } else if (aboveMediumQuery.matches && !document.querySelector('html').classList.contains('desktopMenuOn')) {
      acs.userMenu.build();
      acs.userMenu.desktop();
    }

    belowMediumQuery.addEventListener('change', function (event) {
      if (event.matches) {
        acs.userMenu.build();
        acs.userMenu.mobile();
      }
    });

    aboveMediumQuery.addEventListener('change', function (event) {
      if (event.matches && !document.querySelector('html').classList.contains('desktopMenuOn')) {
        acs.userMenu.build();
        acs.userMenu.desktop();
      }
    });
  };

  acs.userMenu.build = function () {
    if (acs.ui.hasOldMenu || acs.ui.hasNewMenu) {
      var memberMenuTarget = acs.ui.hasOldMenu ? document.getElementById('my-menu') : document.getElementById('member-menu');
      var hasOldMenu = acs.ui.hasOldMenu;
      var user = getAcs()?.user || {};
      var urls = getRuntimeUrls();
      var state = getState(user);
      var donate = hasOldMenu
        ? '<div id="joinLink" class="donate-link" role="menuitem" tabIndex="-1"><a href="' + urls.donate + '">Donate</a></div>'
        : '<li class="js--member-menu__item" role="none"><a class="hat__button--member" role="menuitem" href="' +
          urls.donate +
          '">Donate</a></li>';
      var loginLabel = state.isLoggedIn
        ? 'Login menu, you are logged in as ' + (user.fullname || '')
        : 'Login menu, you are not logged in';
      var login = hasOldMenu
        ? '<div id="loginLink"><a href="' + urls.logIn + '" class="btn-primary btn-block">Log In</a></div>'
        : '<a href="' + urls.logIn + '" class="my-account__login">Log In</a>';
      var logout = hasOldMenu
        ? dropdownItem(true, urls.logOut, 'Log Out', 'logout-link', 'logoutLink')
        : '<a href="' + urls.logOut + '" class="my-account__link">' + (MENU_ICONS['Log Out'] || '') + 'Log Out</a>';
      var join = hasOldMenu
        ? '<div id="membershipLink" class="join-link" role="menuitem" tabIndex="-1"><a href="' + urls.membership + '">Join ACS</a></div>'
        : '<li class="js--member-menu__item" role="none"><a class="hat__button--member hat__button--highlight" role="menuitem" href="' + urls.membership + '">Join ACS</a></li>';
      var name = hasOldMenu
        ? '<h3 class="mobile-title">' + (user.fullname || '') + '</h3>'
        : '<h3 class="my-account__welcome">' + (user.fullname || '') + '</h3>';
      var membership = hasOldMenu
        ? '<div id="membershipLink" class="membership-link" role="menuitem" tabIndex="-1"><a href="' + urls.membership + '">Membership</a></div>'
        : '<li class="js--member-menu__item" role="none"><a class="hat__button--member hat__button--highlight" role="menuitem" href="' + urls.membership + '">Membership</a></li>';
      var account = dropdownItem(hasOldMenu, urls.account, 'Account', 'myaccount-link', 'myAccountLink');
      var benefits = dropdownItem(hasOldMenu, urls.myAcs, 'My Benefits', 'mybenefits-link');
      var refer = dropdownItem(hasOldMenu, urls.friend, 'Refer a Friend', 'refer-link');
      var rewards = dropdownItem(hasOldMenu, urls.rewards, 'Reward Points', 'rewards-link');
      var help = dropdownItem(hasOldMenu, urls.help, 'Help Center', 'help-link');
      var accountButton = hasOldMenu
        ? '<button id="myAccountButton" type="button" tabIndex="0" class="dropdown-toggle login-link" class="' +
          (state.isLoggedIn ? 'account-logged-in' : '') +
          '" data-toggle="dropdown" aria-haspopup="true" aria-owns="myAccountMenu" aria-controls="myAccountMenu" aria-expanded="false" aria-label="' +
          loginLabel +
          '">' +
          (user.fullname ? user.fullname : '<span>My Account</span>') +
          '</button>'
        : '<a href="#" id="my-account-button" class="my-account__button' +
          (state.isLoggedIn ? ' logged-in' : '') +
          '" role="menuitem" aria-haspopup="true" aria-owns="my-account-menu" aria-controls="my-account-menu" aria-expanded="false" aria-label="' +
          loginLabel +
          '"></a>';

      var userMenuHTML = '';

      if (hasOldMenu) {
        userMenuHTML = '<div>';
        userMenuHTML += donate;
        userMenuHTML += '<div class="my-account-section">';
        userMenuHTML += accountButton;
        userMenuHTML +=
          '<div id="myAccountMenu" class="dropdown-menu" aria-expanded="false" aria-labelledby="loginLink" aria-hidden="true" role="menu" tabIndex="-1">';
        userMenuHTML += '<div class="menu-container">';
        if (!state.isLoggedIn) {
          userMenuHTML += login;
        }
        if (state.isLoggedIn) {
          userMenuHTML += name;
        }
        userMenuHTML += '<div class="menu-col single-list">';
        userMenuHTML += '<ul>';
        userMenuHTML += account;
        userMenuHTML += benefits;
        if (state.isLoggedIn) {
          userMenuHTML += refer;
        }
        if (state.isLoggedIn) {
          userMenuHTML += rewards;
        }
        userMenuHTML += help;
        if (state.isLoggedIn) {
          userMenuHTML += logout;
        }
        userMenuHTML += '</ul>';
        userMenuHTML += '</div>';
        userMenuHTML += '</div>';
        userMenuHTML += '</div>';
        userMenuHTML += '</div>';
        if (!state.isLoggedIn) {
          userMenuHTML += join;
        }
        if (state.showMembership) {
          userMenuHTML += membership;
        }
        userMenuHTML += '</div>';
      } else {
        userMenuHTML += '<ul class="members js--member-menu" role="menu">';
        userMenuHTML += donate;
        userMenuHTML += '<li class="js--member-menu__item" role="none">';
        userMenuHTML += accountButton;
        userMenuHTML +=
          '<div id="my-account-menu" class="my-account js--member-menu__sub" aria-expanded="false" aria-hidden="true" aria-expanded="false" aria-labelledby="my-account-button" role="group">';
        if (state.isLoggedIn) {
          userMenuHTML += name;
        } else {
          userMenuHTML += login;
        }
        userMenuHTML += '<div class="my-account__links">';
        userMenuHTML += '<ul class="my-account__list">';
        userMenuHTML += account;
        userMenuHTML += benefits;
        if (state.isLoggedIn) {
          userMenuHTML += refer;
        }
        if (state.isLoggedIn) {
          userMenuHTML += rewards;
        }
        userMenuHTML += help;
        if (state.isLoggedIn) {
          userMenuHTML += logout;
        }
        userMenuHTML += '</ul>';
        userMenuHTML += '</div>';
        userMenuHTML += '</div>';
        userMenuHTML += '</li>';
        if (!state.isLoggedIn) {
          userMenuHTML += join;
        }
        if (state.showMembership) {
          userMenuHTML += membership;
        }
        userMenuHTML += '</ul>';
      }

      memberMenuTarget.innerHTML = userMenuHTML;
    }
  };

  acs.userMenu.desktop = function () {
    if (acs.ui.hasNewMenu) {
      if ($.fn.accessibleMegaMenu) {
        $('#member-menu').accessibleMegaMenu({
          /* prefix for generated unique id attributes, which are required
						 to indicate aria-owns, aria-controls and aria-labelledby */
          uuidPrefix: 'js--member-menu',

          /* css class used to define the megamenu styling */
          menuClass: 'js--member-menu',

          /* css class for a top-level navigation item in the megamenu */
          topNavItemClass: 'js--member-menu__item',

          /* css class for a megamenu panel */
          panelClass: 'js--member-menu__sub',

          /* css class for a group of items within a megamenu panel */
          panelGroupClass: 'js--member-menu-sub__list',

          /* css class for the hover state */
          hoverClass: 'hover',

          /* css class for the focus state */
          focusClass: 'focus',

          /* css class for the open state */
          openClass: 'open',
        });
      }
    }
  };

  acs.userMenu.mobile = function () {
    if ($.fn.accessibleMegaMenu) {
      $('#member-menu').accessibleMegaMenu.destroy;
    }

    let myAccountButton = acs.ui.hasOldMenu ? document.querySelector('#myAccountButton') : document.querySelector('.my-account__button');

    myAccountButton.removeEventListener('click', showMenu);
    myAccountButton.addEventListener('click', showMenu);

    function showMenu() {
      if (!myAccountButton.classList.contains('showit')) {
        if (!!document.querySelector('.acsHeader')) {
          document.querySelector('.acsHeader').classList.remove('menu-on');
        }
        document.querySelectorAll('.showit').forEach(element => element.classList.remove('showit'));
        document.querySelectorAll('.hide').forEach(element => element.classList.remove('hide'));
        if (!!document.querySelector('.menu')) {
          document.querySelector('.menu').classList.remove('search-on');
        }
        document.querySelector('body').classList.remove('noscroll');
      }
      if (!!document.querySelector('.hat__menu-back')) {
        document.querySelector('.hat__menu-back').classList.remove('showit');
      }
      if (!!document.querySelector('.menu__logo')) {
        document.querySelector('.menu__logo').classList.remove('hide');
      }
      document.querySelector('body').classList.toggle('noscroll');
      if (!!document.querySelector('.my-account')) {
        document.querySelector('.my-account').classList.toggle('showit');
      }
      myAccountButton.classList.toggle('showit');
    }
  };

  var methods = {
    init: function () {
      acs.userMenu.init();

      amplify.subscribe('sso.status.changed', function (newData) {
        acs.userMenu.init();
      });
    },
    update: function () {
      acs.userMenu.init();
    },
  };

  // extend the acs namespace with the login menu UI
  Object.assign(acs.ui, { loginMenu: methods });
}
usermenu();
